import { useState, useEffect, useRef } from "react";
import LoginScreen from "./components/LoginScreen";
import POSScreen from "./components/POSScreen";
import EndShiftScreen from "./components/EndShiftScreen";
import AdminScreen from "./components/AdminScreen";
import ShoppingScreen from "./components/ShoppingScreen";
import { getEmployees, saveSale, saveCheckIn, saveCheckOut, getOnline, checkAllowExtraDB, consumeExtraShift, removeAllowExtraDB, getBarToday, processQueue } from "./api";

const ADMIN_PIN = "9999";

function getToday() { return new Date().toLocaleDateString("de-DE"); }
function getTime() { return new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }); }
function newRow() { return { id: Date.now() + Math.random(), model: "", huellePrice: "", glassPrice: "" }; }

export default function App() {
  const [screen, setScreen] = useState("login");
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState(false);
  const [pinMessage, setPinMessage] = useState("");
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [sessionSales, setSessionSales] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [allowExtra, setAllowExtra] = useState([]);
  const [flashSale, setFlashSale] = useState(false);
  const [selling, setSelling] = useState(false);
  const sellingRef = useRef(false);
  const [adminTab, setAdminTab] = useState("sales");
  const [huelle, setHuelle] = useState(false);
  const [glass, setGlass] = useState(false);
  const [rows, setRows] = useState([newRow()]);
  const [zubSelected, setZubSelected] = useState(false);
  const [zubDesc, setZubDesc] = useState("");
  const [zubPrice, setZubPrice] = useState("");
  const [spielzeugSelected, setSpielzeugSelected] = useState(false);
  const [spielzeugDesc, setSpielzeugDesc] = useState("");
  const [spielzeugPrice, setSpielzeugPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [onlineCache, setOnlineCache] = useState([]);

  // Load session from localStorage
  // Save session to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("xbent_session", JSON.stringify({
        currentEmployee, attendance, sessionSales
      }));
    } catch(e) {}
  }, [currentEmployee, attendance, sessionSales]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("xbent_session");
      if (saved) {
        const s = JSON.parse(saved);
        if (s.currentEmployee) setCurrentEmployee(s.currentEmployee);
        if (s.attendance) setAttendance(s.attendance);
        if (s.sessionSales) setSessionSales(s.sessionSales);
        if (s.currentEmployee) { setScreen("pos"); setLoading(false); }
      }
    } catch(e) {}
  }, []);

  useEffect(() => {
    const refreshOnline = () => getOnline().then(r => setOnlineCache(r.online || [])).catch(()=>{});
    refreshOnline();
    const onlineInterval = setInterval(refreshOnline, 30000);
    const queueInterval = setInterval(processQueue, 60000);
    window.addEventListener("online", processQueue);
    return () => { clearInterval(onlineInterval); clearInterval(queueInterval); window.removeEventListener("online", processQueue); };
  }, []);

  useEffect(() => {
    getEmployees()
      .then(data => {
        setEmployees(data);
        setLoading(false);
        try {
          const saved = localStorage.getItem("xbent_session");
          if (saved) {
            const s = JSON.parse(saved);
            if (s.currentEmployee) {
              setCurrentEmployee(s.currentEmployee);
              setAttendance(s.attendance || []);
              setSessionSales(s.sessionSales || []);
              setScreen("pos");
            }
          }
        } catch(e) {}
      })
      .catch(() => { setLoading(false); });
  }, []);

  function resetPOS() {
    setHuelle(false); setGlass(false); setRows([newRow()]);
    setZubSelected(false); setZubDesc(""); setZubPrice("");
    setSpielzeugSelected(false); setSpielzeugDesc(""); setSpielzeugPrice("");
  }

  function handlePinPress(digit, onEmployeeFound) {
    if (pinInput.length >= 4) return;
    const p = pinInput + digit;
    setPinInput(p);
    if (p.length === 4) {
      setTimeout(async () => {
        const emp = employees.find(e => String(e.pin) === p);
        if (emp) {
          let onlineList = [];
          try { const r = await getOnline(); onlineList = r.online || []; setOnlineCache(onlineList); } catch(e) { onlineList = onlineCache; }
          const alreadyIn = onlineList.some(a => a.employeeName.trim() === emp.name.trim());
          const hadShift = attendance.some(a => a.employeeId === emp.id && a.date === getToday() && a.checkOut);
          let isAllowed = allowExtra.includes(emp.id);
          if (!isAllowed) { try { const r2 = await checkAllowExtraDB(emp.name); isAllowed = r2.allowed === true; } catch(e) {} }
          if (alreadyIn) {
            setCurrentEmployee(emp);
            setScreen("pos");
            setPinInput("");
          } else if (hadShift && !isAllowed) {
            setPinMessage("Schicht bereits beendet — Manager kontaktieren");
            setPinError(true);
            setTimeout(() => { setPinError(false); setPinMessage(""); setPinInput(""); }, 2000);
          } else {
            setAllowExtra(prev => prev.filter(id => id !== emp.id));
            try { consumeExtraShift(emp.name); removeAllowExtraDB(emp.name); } catch(e) {}
            onEmployeeFound && onEmployeeFound(emp);
            setPinInput("");
          }
        } else if (p === ADMIN_PIN) {
          setPinInput(""); setScreen("admin");
        } else {
          setPinError(true);
          setTimeout(() => { setPinError(false); setPinInput(""); }, 600);
        }
      }, 150);
    }
  }

  async function handleLogin(emp, startTime) {
    try {
      const r = await saveCheckIn({ date: getToday(), employeeName: emp.name, startTime });
      if (r && r.status === "already_online") {
        startTime = r.startTime || startTime;
      } else if (r && r.status === "blocked") {
        setPinMessage("Ein Mitarbeiter ist bereits eingeloggt");
        setPinError(true);
        setTimeout(() => { setPinError(false); setPinMessage(""); }, 2000);
        return;
      }
    } catch(e) {}
    setCurrentEmployee(emp);
    setAttendance(prev => [...prev, { employeeId: emp.id, employeeName: emp.name, checkIn: new Date(), startTime, checkOut: null, endTime: null, date: getToday() }]);
    setOnlineCache(prev => [...prev, { employeeName: emp.name, startTime }]);
    setScreen("pos");
  }

  async function handleSale(paymentMethod) {
    if (sellingRef.current) return;
    sellingRef.current = true;
    setSelling(true);
    try {
      const newSales = []; const txId = Date.now(); let idx = 0;
      rows.forEach(row => {
        if (huelle && row.huellePrice && parseFloat(row.huellePrice) > 0)
          newSales.push({ id: txId+idx++, txId, employeeId: currentEmployee.id, employeeName: currentEmployee.name, category: "Huelle", description: row.model, price: parseFloat(row.huellePrice), payment: paymentMethod, date: getToday(), time: getTime() });
        if (glass && row.glassPrice && parseFloat(row.glassPrice) > 0)
          newSales.push({ id: txId+idx++, txId, employeeId: currentEmployee.id, employeeName: currentEmployee.name, category: "Glass", description: row.model, price: parseFloat(row.glassPrice), payment: paymentMethod, date: getToday(), time: getTime() });
      });
      if (zubSelected && zubPrice && parseFloat(zubPrice) > 0)
        newSales.push({ id: txId+idx++, txId, employeeId: currentEmployee.id, employeeName: currentEmployee.name, category: "Zubehoer", description: zubDesc, price: parseFloat(zubPrice), payment: paymentMethod, date: getToday(), time: getTime() });
      if (spielzeugSelected && spielzeugPrice && parseFloat(spielzeugPrice) > 0)
        newSales.push({ id: txId+idx++, txId, employeeId: currentEmployee.id, employeeName: currentEmployee.name, category: "Spielzeug", description: spielzeugDesc, price: parseFloat(spielzeugPrice), payment: paymentMethod, date: getToday(), time: getTime() });
      if (!newSales.length) return;
      resetPOS();
      setSessionSales(prev => [...prev, ...newSales]);
      setFlashSale(true); setTimeout(() => setFlashSale(false), 800);
      for (const sale of newSales) await saveSale(sale);
    } catch(e) {
      console.error(e);
    } finally {
      sellingRef.current = false;
      setSelling(false);
    }
  }

  async function confirmEndShift(endTime) {
    try {
      const att = attendance.find(a => a.employeeId === currentEmployee.id && !a.checkOut);
      if (att && att.startTime) {
        const start = att.startTime.split(":").map(Number);
        const end = endTime.split(":").map(Number);
        const hours = ((end[0]*60+end[1]) - (start[0]*60+start[1])) / 60;
        saveCheckOut({ date: getToday(), employeeName: currentEmployee.name, endTime, hours: hours.toFixed(1) });
      }
    } catch(e) {
      console.error("confirmEndShift error:", e);
    } finally {
      setAttendance(prev => prev.map(a => a.employeeId === currentEmployee.id && !a.checkOut ? { ...a, checkOut: new Date(), endTime } : a));
      localStorage.removeItem("xbent_session"); setOnlineCache([]); setCurrentEmployee(null); resetPOS(); setScreen("login");
    }
  }

  const todayBarTotal = sessionSales.filter(s => s.payment === "Bar").reduce((sum,s) => sum+s.price, 0);
  const [allDayBarTotal, setAllDayBarTotal] = useState(0);
  useEffect(() => {
    if (screen === "endshift") {
      getBarToday().then(data => {
        setAllDayBarTotal(data.barTotal || 0);
      }).catch(()=>{});
    }
  }, [screen]);
  const todayTotal = sessionSales.reduce((sum,s) => sum+s.price, 0);
  const canSell = !selling && (
    rows.some(r => (huelle && r.huellePrice && parseFloat(r.huellePrice) > 0) || (glass && r.glassPrice && parseFloat(r.glassPrice) > 0)) ||
    (zubSelected && zubPrice && parseFloat(zubPrice) > 0) ||
    (spielzeugSelected && spielzeugPrice && parseFloat(spielzeugPrice) > 0)
  );

  if (loading) return (
    <div style={{ backgroundColor:"#0A0A0A", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ color:"#00E5FF", fontFamily:"'Space Mono',monospace", fontSize:16 }}>LADEN...</div>
    </div>
  );

  return (
    <div style={{ backgroundColor:"#0A0A0A", minHeight:"100vh", maxWidth:"420px", margin:"0 auto", fontFamily:"'Barlow Condensed',sans-serif", color:"#FFF", userSelect:"none" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        @keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-8px)}40%,80%{transform:translateX(8px)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes popIn{0%{transform:scale(0.94);opacity:0}100%{transform:scale(1);opacity:1}}
        .shake{animation:shake .5s ease}.fade-in{animation:fadeIn .25s ease}.pop-in{animation:popIn .18s ease}
        input,textarea{background:#161616;border:1.5px solid #2A2A2A;color:#FFF;border-radius:12px;padding:11px 14px;font-family:'Barlow Condensed',sans-serif;font-size:16px;width:100%;outline:none;transition:border-color .2s}
        input:focus,textarea:focus{border-color:#00E5FF}
        textarea{resize:none}
        ::-webkit-input-placeholder{color:#444}
        input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none} input[type=number]{-moz-appearance:textfield}
        button{-webkit-tap-highlight-color:transparent}
        ::-webkit-scrollbar{width:0}
      `}</style>
      {screen==="login" && <LoginScreen pinInput={pinInput} pinError={pinError} pinMessage={pinMessage} onPress={handlePinPress} onDelete={()=>setPinInput(p=>p.slice(0,-1))} onLogin={handleLogin} />}
      {screen==="pos" && <POSScreen employee={currentEmployee} huelle={huelle} setHuelle={setHuelle} glass={glass} setGlass={setGlass} rows={rows} setRows={setRows} updateRow={(id,f,v)=>setRows(p=>p.map(r=>r.id===id?{...r,[f]:v}:r))} zubSelected={zubSelected} setZubSelected={setZubSelected} zubDesc={zubDesc} setZubDesc={setZubDesc} zubPrice={zubPrice} setZubPrice={setZubPrice} spielzeugSelected={spielzeugSelected} setSpielzeugSelected={setSpielzeugSelected} spielzeugDesc={spielzeugDesc} setSpielzeugDesc={setSpielzeugDesc} spielzeugPrice={spielzeugPrice} setSpielzeugPrice={setSpielzeugPrice} onSale={handleSale} onEndShift={()=>setScreen("endshift")} onShopping={()=>setScreen("shopping")} todayTotal={todayTotal} salesCount={sessionSales.length} flashSale={flashSale} canSell={canSell} newRow={newRow} />}
      {screen==="shopping" && <ShoppingScreen employee={currentEmployee} onBack={()=>setScreen("pos")} />}
      {screen==="endshift" && <EndShiftScreen employee={currentEmployee} barTotal={allDayBarTotal > 0 ? allDayBarTotal : todayBarTotal} totalSales={sessionSales.length} totalRevenue={todayTotal} onConfirm={confirmEndShift} onBack={()=>setScreen("pos")} />}
      {screen==="admin" && <AdminScreen sales={sessionSales} attendance={attendance} employees={employees} setEmployees={setEmployees} allowExtra={allowExtra} setAllowExtra={setAllowExtra} activeTab={adminTab} setActiveTab={setAdminTab} onLogout={()=>setScreen("login")} />}
    </div>
  );
}
